import LocalStorage from '@webcore/core/localstorage/localstorage';
import { useEffect, useState } from 'react';

const localStorage = new LocalStorage();

function useLocalStorage<T>(key: string, initialValue:T | null) {
  const [storedValue, setStoredValue] = useState<T | null>(initialValue);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchLocalStorage = async () => {
      try {
          const item = await localStorage.getItemAsync(key);
          if (item !== null) {
            const isObject = typeof initialValue === 'object' && initialValue !== null;
            const parsedItem = isObject ? JSON.parse(item) : (() => {
              try { return JSON.parse(item); } catch { return item; }
            })();
            setStoredValue(parsedItem as T);
          } else {
            setStoredValue(initialValue);
          }

      } catch (error) {
        console.error('Error fetching from localStorage:', error);
        setStoredValue(initialValue);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLocalStorage();
  }, [key]);

  const setValue = async (value: T | ((val: T | null) => T)) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      await localStorage.setItemAsync(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.error('Error setting value to localStorage:', error);
    }
  };

  const clearValue = async () => {
    try {
      await localStorage.removeItemAsync(key);
      setStoredValue(null);
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  };

  return [storedValue, setValue, clearValue, isLoading] as const;
}

export default useLocalStorage;